<script lang="ts">
import { defineComponent, onUnmounted } from 'vue'

import { opChain } from '@collector/shared-utils'
import { BasketballIncidentId } from '@collector/sportsapi-client-legacy'
import PossessionButton from '@mobile/components/PossesionButton/PossessionButton.vue'
import { useEventStatus } from '@mobile/views/Relation/Shared/EventStatus/useEventStatus'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import { useSportConfiguration } from '@mobile/views/Relation/Shared/RelationSportCommonDependencies/useSportConfiguration'
import { usePitchOverlays } from '@mobile/views/Relation/Sports/Basketball/LiveView/Pitch/usePitchOverlays'

import { useBasketBallConfiguration } from '../../BasketballRelationDependencies/useBasketBallConfiguration'
import { useTimeoutOver } from './useTimeoutOver'

export default defineComponent({
  components: { PossessionButton },
  emits: ['sidesSwitched'],
  setup() {
    const {
      probableFirstParticipant,
      probableSecondParticipant,
      sidesSwitched,
      probableEvent,
      actionQueue,
      probableIncidents,
      notDeletableProbableIncidents,
      probableClock,
    } = useEventInjections()
    const { incidentsConfiguration } = useSportConfiguration()
    const { overlayComponents, visibleOverlay } = usePitchOverlays()
    const { isMatchStarted } = useEventStatus(probableEvent)
    const { activeParticipant } = useBasketBallConfiguration()

    const { cleanup: cleanupUseTimeoutOver } = useTimeoutOver(
      actionQueue.value,
      probableIncidents,
      notDeletableProbableIncidents,
      probableClock,
      incidentsConfiguration,
    )

    onUnmounted(() => {
      cleanupUseTimeoutOver()
    })

    return {
      probableFirstParticipant,
      probableSecondParticipant,
      overlayComponents,
      visibleOverlay,
      sidesSwitched,
      isMatchStarted,
      activeParticipant,
      BasketballIncidentId,
      opChain,
    }
  },
})
</script>

<template>
  <div
    class="relative col-start-1 col-end-8 row-start-2 row-end-5 flex h-full w-full"
  >
    <div class="bg-basketball-bottom relative w-full overflow-hidden bg-cover">
      <img
        ref="pitchTopRef"
        class="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform sm:w-full"
        :class="!isMatchStarted && 'h-full'"
        src="@mobile/assets/pitches/basketball/top.svg"
      />

      <div
        v-if="!visibleOverlay"
        class="absolute inset-0 flex h-full w-full flex-col space-y-2 p-1"
      >
        <div class="flex h-full w-full px-2 py-2">
          <PossessionButton
            class="flex-1"
            :participant="probableFirstParticipant"
            :activeParticipant
            :inPossessionIncidentId="BasketballIncidentId.InPossession"
          />
          <PossessionButton
            class="flex-1"
            :participant="probableSecondParticipant"
            :activeParticipant
            :inPossessionIncidentId="BasketballIncidentId.InPossession"
          />
        </div>
      </div>

      <div
        v-for="(overlayComponent, index) in overlayComponents"
        v-show="
          overlayComponent.props.incidentId ===
          opChain(visibleOverlay, (v) => v.props.incidentId)
        "
        :key="index"
      >
        <component
          :is="overlayComponent.component"
          v-bind="overlayComponent.props"
          @sidesSwitched="$emit('sidesSwitched')"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pitch-bottom {
  background-size: 100% 100%;

  @media only screen and (min-width: 1024px) {
    background-position: center center;
    background-size: 300%;
  }
}
</style>
